import { useEffect, useRef } from "react";
import { toast } from "sonner";
import type { DebateMap } from "@/hooks/useFieldDebate";

/** Stable toast ids so the overview and the FD view (both may be mounted)
 *  announce a landed refresh exactly once. */
export function refreshLandedToastId(conceptId: string, refreshedAt: string): string {
  return `fd-refreshed-${conceptId}-${refreshedAt}`;
}
export function refreshErrorToastId(conceptId: string): string {
  return `fd-refresh-error-${conceptId}`;
}

/**
 * Announce a batch refresh landing when the SERVER-reported pending_build
 * flips from "refresh" to nothing. Covers the reload case, where the
 * in-memory pendingRefresh flag (and its own toast path) no longer exists.
 */
export function useFieldDebateRefreshLanded(
  conceptId: string,
  map: DebateMap | null | undefined,
): void {
  const prev = useRef<{ kind: string | null; submittedAt: string | null }>({
    kind: null,
    submittedAt: null,
  });
  useEffect(() => {
    const kind = map?.pending_build?.kind ?? null;
    const was = prev.current;
    prev.current = { kind, submittedAt: map?.pending_build?.submitted_at ?? null };
    if (was.kind !== "refresh" || kind !== null || !map) return;
    if (map.refresh_error) {
      toast.error(map.refresh_error, { id: refreshErrorToastId(conceptId) });
      return;
    }
    // Only a refresh_meta stamped AFTER the pending build was submitted is
    // THIS refresh landing. An older one means the build died without a
    // map-level error (defensive: the backend stamps refresh_error on submit
    // failure, but never claim success off stale metadata).
    const meta = map.refresh_meta;
    if (!meta?.refreshed_at) return;
    if (
      was.submittedAt &&
      Date.parse(meta.refreshed_at) < Date.parse(was.submittedAt)
    ) {
      return;
    }
    toast.success(
      `Map refreshed — ${meta.added} paper${meta.added === 1 ? "" : "s"} added.`,
      { id: refreshLandedToastId(conceptId, meta.refreshed_at) },
    );
  }, [conceptId, map]);
}
